import { Component, Output, EventEmitter, NgZone, computed, effect, signal, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { Delivery } from '@app/common-codes/models/matwins-tenant/Delivery.model';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { DateTimePickerComponent } from '@app/common-codes/components/nested-components/date-time-picker/date-time-picker.component';

@Register(Delivery)
@Component({
  selector: 'app-delivery-detail',
  templateUrl: './delivery-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, MultiSelectComponent, FieldWrapperComponent, DateTimePickerComponent, FieldContextDirective]
})
export class DeliveryDetailComponent extends GenetricDetailComponent<Delivery> {
  override ComponentEntityClass = Delivery;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  @Input() fkPointOfServiceId: number | null = null;
  @Output() itemSaved = new EventEmitter<Delivery>();
  protected override imageRelationName = 'FK_Image_Package';

  // Placeholders traduits — des signaux mis à jour à chaque changement de langue
  recipientPlaceholder = signal(this.translationService.instant('vues.delivery.detail.select-recipient'));
  pickupByPlaceholder = signal(this.translationService.instant('vues.delivery.detail.select-pickup-by'));

  deliveryStatusLookup = signal<LookupItem[]>([]);
  translatedStatusLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);
  employeeLookup = signal<LookupItem[]>([]);

  /** Destinataire et Recupere par : employes rattaches au POS courant (meme regle que Visite). */
  readonly filteredEmployeeLookup = computed(() => {
    const employees = this.employeeLookup();
    const posId = this.editingEntity()?.fK_PointOfService;
    if (!employees || !posId) return employees;

    return employees.filter(employee =>
      `${employee.additionalData['pos_ids']}`.split(',').some(st => st.trim() === `${posId}`));
  });

  // Delivery type : null = non défini, 1 = Parcel, 2 = Meal
  selectedDeliveryType = signal<number | null>(null);

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    // Mettre à jour les placeholders à chaque changement de langue
    translationService.currentLanguage$.subscribe(() => {
      this.recipientPlaceholder.set(translationService.instant('vues.delivery.detail.select-recipient'));
      this.pickupByPlaceholder.set(translationService.instant('vues.delivery.detail.select-pickup-by'));
    });

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        const rawStatus = this.lookupService.getLookupData('DeliveryStatus');
        this.deliveryStatusLookup.set(rawStatus);
        this.translatedStatusLookup.set(rawStatus.map(item => ({
          ...item,
          value: this.translationService.instant(`entities.delivery-status.${(item.value || '').toLowerCase().replace(/\s+/g, '-')}`)
        })));
        this.pointOfServiceLookup.set(this.getLookupData('PointOfService'));
        this.employeeLookup.set(this.getLookupData('Employee'));
        this.loadPos();
        this.selectedDeliveryType.set(entity.deliveryType ?? null);

        // Restaurer l'image package après le reset du parent
        if (entity.imagePackage?.thumbnail_Path) {
          this.imageUrl = '/Documents/' + entity.imagePackage.thumbnail_Path;
        }
      }
    });
  }

  override getEmptyEntity(): Delivery {
    const delivery = new Delivery();
    delivery.status = 1; // Available (ready-for-pickup)
    delivery.numberOfNotificationSent = 0;
    delivery.deliveryDate = new Date();
    if (this.fkPointOfServiceId) {
      delivery.fK_PointOfService = this.fkPointOfServiceId;
    }
    if (this.matwinsVariablesService.posId) {
      delivery.fK_PointOfService = this.matwinsVariablesService.posId;
    }
    return delivery;
  }

  loadPos() {
    const entity = this.editingEntity();
    if (!entity) return;
    const selected = entity.fK_PointOfService;
    if (selected) entity.selectedPK_Pos = [selected.toString()];
    if (entity.fK_Employee) entity.selectedPK_Employee = [entity.fK_Employee.toString()];
    if (entity.fK_PickupBy) entity.selectedPK_PickupBy = [entity.fK_PickupBy.toString()];
  }

  onPOSselectChange(evt: string[]) {
    // Sauvegarder l'image avant que l'effect du parent ne la reset
    const savedImageUrl = this.imageUrl;
    const savedCurrentImage = this.currentImage;

    this.editingEntity.update(entity => {
      if (!entity) return entity;
      const selectedPosIds = (evt ?? []).map(k => Number(k)).filter(n => Number.isFinite(n));
      entity.fK_PointOfService = selectedPosIds[0] ?? null;
      entity.selectedPK_Pos = evt;
      entity.fK_Employee = undefined;
      entity.employee = undefined;
      entity.selectedPK_Employee = [];
      entity.fK_PickupBy = undefined;
      entity.pickupByEmployee = undefined;
      entity.selectedPK_PickupBy = [];
      return entity;
    });

    // Restaurer l'image après le reset du parent
    setTimeout(() => {
      this.imageUrl = savedImageUrl;
      this.currentImage = savedCurrentImage;
    }, 0);
  }

  onRecipientSelectChange(evt: string[]) {
    const entity = this.editingEntity();
    if (!entity) return;
    const selectedIds = (evt ?? []).map(k => Number(k)).filter(n => Number.isFinite(n));
    entity.fK_Employee = selectedIds[0] ?? undefined;
    entity.selectedPK_Employee = evt;
    if (selectedIds[0]) {
      const emp = this.employeeLookup().find(e => Number(e.key) === selectedIds[0]);
      entity.employeeName = emp?.value ?? '';
    }
  }

  onPickupBySelectChange(evt: string[]) {
    const entity = this.editingEntity();
    if (!entity) return;
    const selectedIds = (evt ?? []).map(k => Number(k)).filter(n => Number.isFinite(n));
    entity.fK_PickupBy = selectedIds[0] ?? undefined;
    entity.selectedPK_PickupBy = evt;
    if (selectedIds[0]) {
      const emp = this.employeeLookup().find(e => Number(e.key) === selectedIds[0]);
      entity.pickupBy = emp?.value ?? '';
    }
  }

  onStatusChange(newStatus: number): void {
    this.editingEntity.update(entity => {
      if (entity) entity.status = newStatus;
      return entity;
    });
  }

  onDeliveryTypeChange(type: number): void {
    this.selectedDeliveryType.set(type);
    this.editingEntity.update(entity => {
      if (entity) entity.deliveryType = type;
      return entity;
    });
  }

  override parseToEntityInstance(): void {
    const target = this.EntityInstance;
    const entity = this.editingEntity();
    if (!target || !entity) return;

    // Les navigations sont retirees du payload : on garde celles de la liste pour l'affichage.
    const { pointOfService, employee, pickupByEmployee, imageSignature, imagePackage } = target;

    Object.assign(target, entity);

    target.pointOfService = pointOfService;
    target.employee = employee;
    target.pickupByEmployee = pickupByEmployee;
    target.imageSignature = imageSignature;
    target.imagePackage = imagePackage;
  }

  private cleanNavigationBackReferences(): void {
    const entity = this.editingEntity();
    if (!entity) return;
    // Les FK scalaires portent la relation : aucune navigation ne doit partir dans le payload.
    entity.pointOfService = undefined;
    entity.employee = undefined;
    entity.pickupByEmployee = undefined;
    entity.imageSignature = undefined;
    entity.imagePackage = undefined;
  }

  async saveItem(): Promise<void> {
    this.cleanNavigationBackReferences();
    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
    }
  }

  async saveAndCloseItem(): Promise<void> {
    this.cleanNavigationBackReferences();
    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }
}
